/* eslint-disable react/prop-types */
/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import DraggableTable from '@components/v2/DraggableTable';
import { TableSize } from '@components/Table';
import { translate } from '@utils/i18n';
import { Button } from '@ui';
import TooltipText from '@components/v2/TooltipText';
import snakeToCapitalize from '@utils/functions/snakeToCapitalize';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import { useDeleteComponentStatusMutation, useReorderComponentStatusesMutation } from './hooks/useComponentStatusesMutations';
const namespace = 'features.settings.component_statuses';
function StatusRenderer({ item, onStatusClick }) {
    const handleClick = () => {
        if (onStatusClick) {
            onStatusClick(item);
        }
    };
    const handleKeyDown = (event) => {
        if ((event.key === 'Enter' || event.key === ' ') && onStatusClick) {
            event.preventDefault();
            onStatusClick(item);
        }
    };
    return (React.createElement("div", { role: "button", "aria-label": item.name, className: "component-status-row", onClick: handleClick, onKeyDown: handleKeyDown, tabIndex: onStatusClick ? 0 : -1, style: { cursor: onStatusClick ? 'pointer' : 'default' } },
        React.createElement("div", { className: "component-status-dot", style: { backgroundColor: item.color || '#6b7280' } }),
        React.createElement("span", null,
            snakeToCapitalize(item.name),
            " (",
            item.componentsCount,
            ")")));
}
function DefaultRenderer({ item, onSetDefaultClick }) {
    const handleDefaultChange = () => {
        // Only allow setting as default if not already default
        if (!item.isDefault && onSetDefaultClick) {
            onSetDefaultClick(item);
        }
    };
    return (React.createElement("div", { className: "component-default-row" },
        React.createElement("input", { type: "radio", name: "default-component-status", checked: item.isDefault, onChange: handleDefaultChange, disabled: item.isDefault, "aria-label": `Set ${item.name} as default status`, style: { cursor: item.isDefault ? 'default' : 'pointer' } })));
}
function ActionsRenderer({ item, onEditClick }) {
    const editLabel = translate('edit', { namespace: 'generic' });
    const deleteLabel = translate('delete', { namespace: 'generic' });
    const deleteMutation = useDeleteComponentStatusMutation();
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    // Check if edit button should be disabled (system status)
    const isEditDisabled = item.isSystemStatus;
    const editTooltip = isEditDisabled ? translate('tooltips.cannot_edit_system_status', { namespace }) : '';
    // Check if delete button should be disabled (system status)
    const isDeleteDisabled = item.isSystemStatus;
    const deleteTooltip = (() => {
        if (item.isSystemStatus) {
            return translate('tooltips.cannot_delete_system_status', { namespace });
        }
        return '';
    })();
    const handleEditClick = () => {
        if (onEditClick && !isEditDisabled) {
            onEditClick(item);
        }
    };
    const handleDeleteClick = () => {
        if (isDeleteDisabled) {
            return;
        }
        setIsDeleteConfirmOpen(true);
    };
    const handleConfirmDelete = () => {
        deleteMutation.mutate(item.id);
        setIsDeleteConfirmOpen(false);
    };
    const handleCancelDelete = () => {
        setIsDeleteConfirmOpen(false);
    };
    return (React.createElement("div", { className: "component-actions-row" },
        React.createElement(TooltipText, { text: editTooltip, position: "top" },
            React.createElement(Button, { "aria-label": `${editLabel} ${item.name}`, disabled: isEditDisabled, onClick: handleEditClick },
                React.createElement("i", { className: "fa-light fa-pencil", "aria-hidden": "true" }))),
        React.createElement(TooltipText, { text: deleteTooltip, position: "top" },
            React.createElement(Button, { "aria-label": `${deleteLabel} ${item.name}`, disabled: isDeleteDisabled, onClick: handleDeleteClick },
                React.createElement("i", { className: "fa-light fa-trash", "aria-hidden": "true" }))),
        isDeleteConfirmOpen && (React.createElement(ConfirmDialog, { title: translate('delete_status_title', { namespace }), description: translate('delete_status_description', { namespace }), onConfirm: handleConfirmDelete, onCancel: handleCancelDelete, yesLabel: translate('delete', { namespace: 'features.commons' }), noLabel: translate('cancel', { namespace: 'features.commons' }) }))));
}
export default function ComponentStatusesTable({ componentStatuses, onStatusClick, onEditClick, onSetDefaultClick }) {
    const reorderMutation = useReorderComponentStatusesMutation();
    const columns = [
        {
            label: translate('default_column', { namespace }),
            dataKey: 'isDefault',
            renderer: (props) => (React.createElement(DefaultRenderer, { item: props.item, onSetDefaultClick: onSetDefaultClick }))
        },
        {
            label: translate('status_column', { namespace }),
            dataKey: 'name',
            renderer: (props) => React.createElement(StatusRenderer, { item: props.item, onStatusClick: onStatusClick })
        },
        {
            label: translate('description_column', { namespace }),
            dataKey: 'description',
            renderer: (props) => React.createElement("span", null, props.item.description || '--')
        },
        {
            label: '',
            dataKey: 'actions',
            renderer: (props) => React.createElement(ActionsRenderer, { item: props.item, onEditClick: onEditClick })
        }
    ];
    const sortedComponentStatuses = [...componentStatuses].sort((a, b) => a.position - b.position);
    const handlePositionChange = (reorderedItems) => {
        reorderMutation.mutate(reorderedItems);
    };
    return (React.createElement(DraggableTable, { columns: columns, data: sortedComponentStatuses, size: TableSize.Full, variant: "detail", getItemId: (item) => item.id, onPositionChange: handlePositionChange }));
}
